import {
  action,
  atom,
  batch,
  isAbortError,
  onDisconnect,
  reatomAsync,
  withAbort,
  withAssign,
} from '@reatom/framework'
import { match } from 'ts-pattern'

import { Array, AsyncStateN, Result } from '@blade/std-lib'
import { Equivalence } from '@blade/std-lib'

import { reatomAsyncState } from './reatom-async-state'
import { reatomResource } from './reatom-resource'

import type { ReatomResourceOptions } from './reatom-resource'
import type { Order } from '@blade/std-lib'
import type { Atom, CtxSpy } from '@reatom/framework'

export type PaginationStrategy = 'cursor' | 'offset'

export type PaginatedData<T, Strategy extends PaginationStrategy> = {
  data: Array<T>
  hasNext?: boolean
  total?: number
} & (Strategy extends 'cursor' ? { nextCursor: string | undefined } : {})

export type PaginatedParams<Strategy extends PaginationStrategy> = {
  limit: number
} & (Strategy extends 'cursor'
  ? { cursor: string | undefined }
  : { offset: number })

export type CursorPaginatedParams = PaginatedParams<'cursor'>
export type OffsetPaginatedParams = PaginatedParams<'offset'>

type ResourcePaginatedOptions<
  E,
  A,
  Strategy extends PaginationStrategy = 'offset',
> = {
  strategy?: Strategy
  limit?: number
  order?: Order.Order<A>
} & ReatomResourceOptions<E, A>

type PaginatedState<A, Strategy extends PaginationStrategy> = {
  nextParams: PaginatedParams<Strategy>
} & PaginatedData<A, Strategy>

export const reatomResourcePaginated = <
  E,
  A,
  Strategy extends PaginationStrategy = 'offset',
>(
  fn: (
    ctx: CtxSpy,
    params: PaginatedParams<NoInfer<Strategy>>,
  ) => Promise<Result.Result<PaginatedData<A, NoInfer<Strategy>>, E>>,
  {
    name,
    limit = 20,
    strategy = 'offset' as Strategy,
    eq = Equivalence.strictEqual(),
    order,
    ...resourceOptions
  }: ResourcePaginatedOptions<E, A, Strategy>,
) => {
  type Data = PaginatedData<A, Strategy>
  type State = PaginatedState<A, Strategy>
  type Params = PaginatedParams<Strategy>

  const initParams: Params = {
    limit,
    cursor: undefined,
    offset: 0,
  }

  //#region State
  // const paginationAtom = atom<Params>(initParams, `${name}.paginationAtom`)

  const loadMoreStateAtom = reatomAsyncState.notInitiated<E, Data>(
    `resource-paginated.${name}.load-more-state`,
  )

  const enrichPaginationParams = (prevParams: Params, data: Data): State => {
    const nextParams = match(strategy as PaginationStrategy)
      .with(
        'cursor',
        () =>
          ({
            ...prevParams,
            cursor: 'nextCursor' in data ? data.nextCursor : undefined,
          }) as PaginatedParams<Strategy>,
      )
      .with(
        'offset',
        () =>
          ({
            ...prevParams,
            offset:
              'offset' in prevParams ? prevParams.offset + prevParams.limit : 0,
          }) as PaginatedParams<Strategy>,
      )
      .exhaustive()

    return {
      ...data,
      nextParams,
    }
  }
  const stateAtom = reatomResource<E, State>(
    async (ctx: CtxSpy) => {
      loadMoreAction.abort(ctx)
      const params = initParams
      // https://t.me/reatom_ru/100819
      // eslint-disable-next-line @reatom/async-rule
      const result = await fn(ctx, params)
      const mappedResult = result.pipe(
        Result.map((it) => enrichPaginationParams(params, it)),
        Result.map(
          (it): PaginatedState<A, Strategy> =>
            order ? { ...it, data: Array.sort(order)(it.data) } : it,
        ),
      )
      return mappedResult
    },
    // eslint-disable-next-line @reatom/unit-naming-rule
    {
      ...resourceOptions,
      eq: getEq(Array.makeEquivalence(eq)),
      name: `resource-paginated.${name}.state`,
    },
  )
  //#endregion

  //#region Computed

  const hasMoreAtom = atom((ctx) => {
    const state = ctx.spy(stateAtom)
    if (!AsyncStateN.isSuccess(state)) {
      return false
    }
    if (typeof state.value.hasNext === 'boolean') {
      return state.value.hasNext
    }
    if ('nextCursor' in state.value) {
      return state.value.nextCursor !== undefined
    }
    if (state.value.total && state.value.data.length >= state.value.total) {
      return false
    }
    return state.value.data.length >= limit
  }, `${name}.hasMoreAtom`)

  const countAtom = atom((ctx) => {
    const state = ctx.spy(stateAtom)
    return AsyncStateN.isSuccess(state) ? state.value.data.length : 0
  }, `${name}.countAtom`)

  const paginationAtom = atom((ctx) => {
    const state = ctx.spy(stateAtom)
    return AsyncStateN.isSuccess(state) ? state.value.nextParams : initParams
  }, `${name}.paginationAtom`)

  //#endregion

  //#region Actions

  const loadMoreAction = reatomAsync(async (ctx) => {
    const state = ctx.get(stateAtom)
    const hasMore = ctx.get(hasMoreAtom)
    if (
      !AsyncStateN.isSuccess(state) ||
      !hasMore ||
      ctx.get(stateAtom.isRefreshing)
    ) {
      return
    }

    loadMoreStateAtom.setLoading(ctx)
    const params = ctx.get(paginationAtom)

    const ctxSpy: CtxSpy = {
      ...ctx,
      spy: ctx.get,
    }
    const result = await ctx.schedule(() => fn(ctxSpy, params))

    batch(ctx, () => {
      const state = ctx.get(stateAtom)
      if (!AsyncStateN.isSuccess(state)) {
        return
      }

      if (Result.isSuccess(result)) {
        loadMoreStateAtom.setSuccess(ctx, result.success)
        const unionData = [...state.value.data, ...result.success.data]
        const updatedData = order ? Array.sort(order)(unionData) : unionData
        const updatedState: Data = {
          ...result.success,
          data: updatedData,
        }
        stateAtom.setSuccess(ctx, enrichPaginationParams(params, updatedState))
        return
      }

      if (isAbortError(result.failure)) {
        loadMoreStateAtom.set(ctx, AsyncStateN.notInitiated())
        return
      }
      loadMoreStateAtom.setError(ctx, result.failure)
    })
  }, `${name}.loadMoreAction`).pipe(withAbort())

  //#endregion

  const resetAction = action(async (ctx) => {
    stateAtom.set(ctx, AsyncStateN.notInitiated())
    loadMoreStateAtom.set(ctx, AsyncStateN.notInitiated())
  }, `${name}.resetAction`)

  onDisconnect(stateAtom, (ctx) => {
    if (resourceOptions.resetOnDisconnect) {
      loadMoreStateAtom.set(ctx, AsyncStateN.notInitiated())
    }
  })

  return stateAtom.pipe(
    withAssign(() => ({
      __resourcePaginated: true,
      pagination: paginationAtom,
      loadMore: loadMoreAction,
      loadMoreState: loadMoreStateAtom,
      hasMore: hasMoreAtom,
      reset: resetAction,
      count: countAtom,
    })),
  )
}
export const isPaginatedResource = <
  E,
  A,
  Strategy extends PaginationStrategy = 'offset',
>(
  atom: Atom<unknown>,
): atom is ResourcePaginated<E, A, Strategy> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (atom as any).__resourcePaginated === true
}

export interface ResourcePaginated<
  E,
  A,
  Strategy extends PaginationStrategy = 'offset',
> extends ReturnType<typeof reatomResourcePaginated<E, A, Strategy>> {}

const getEq =
  <A>(
    eq: Equivalence.Equivalence<Array<A>>,
  ): Equivalence.Equivalence<PaginatedData<A, PaginationStrategy>> =>
  (left, right) => {
    return (
      left.hasNext === right.hasNext &&
      left.total === right.total &&
      (left as PaginatedData<A, 'cursor'>).nextCursor ===
        (right as PaginatedData<A, 'cursor'>).nextCursor &&
      eq(left.data, right.data)
    )
  }
